import { Box, Stack, Typography } from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { Field, StepCard, StepTitle } from '../../components/ui'
import { invalidEmails } from '../../lib/emailList'
import { SHOW_TAX_PAYMENT_CONTACT } from '../../lib/prototypeSettings'
import type { ClientContacts } from '../../types/onboarding'

const MULTI_EMAIL_HINT =
  'Enter one or more email addresses separated by commas. General inboxes (for example AP or bill.com addresses) are welcome — include everyone who should be copied.'

interface ContactQuestion {
  field: keyof ClientContacts
  question: string
  instructions: string
  placeholder: string
}

const QUESTIONS: ContactQuestion[] = [
  {
    field: 'invoiceEmails',
    question: "Where should MISC's monthly service invoice be sent?",
    instructions: MULTI_EMAIL_HINT,
    placeholder: 'ap@yourcompany.com, controller@yourcompany.com',
  },
  {
    field: 'portalAdminEmails',
    question: 'Who should be the client admin for the Surplus Lines Tax Portal?',
    instructions:
      'This person receives the admin invitation to the portal and can then add your team members and assign their roles. Separate multiple addresses with commas.',
    placeholder: 'jane.smith@yourcompany.com',
  },
  {
    field: 'taxPaymentEmails',
    question: 'Who is responsible for making sure your surplus lines taxes are paid?',
    instructions: `Your accounting contact for surplus lines tax payments to the states. ${MULTI_EMAIL_HINT}`,
    placeholder: 'taxpayables@yourcompany.com',
  },
]

/** Contacts (MISC Onboarding Sync): three labeled questions, each a single comma-separated email field. */
export function StepContacts() {
  const { state, dispatch } = useOnboarding()
  const questions = QUESTIONS.filter((q) => q.field !== 'taxPaymentEmails' || SHOW_TAX_PAYMENT_CONTACT)

  return (
    <StepCard>
      <StepTitle subtitle="Tell us where to send billing and portal information for your organization.">Contacts</StepTitle>
      <Stack spacing={3.5}>
        {questions.map((q, i) => {
          const value = state.contacts[q.field]
          const invalid = invalidEmails(value)
          return (
            <Box key={q.field}>
              <Typography component="label" htmlFor={`contact-${q.field}`} sx={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#0f172a' }}>
                {i + 1}. {q.question}
              </Typography>
              <Typography sx={{ mt: 0.5, mb: 1.25, fontSize: 13, lineHeight: 1.6, color: '#475569' }}>{q.instructions}</Typography>
              <Field
                id={`contact-${q.field}`}
                type="text"
                inputMode="email"
                value={value}
                placeholder={q.placeholder}
                error={invalid.length > 0}
                helperText={invalid.length > 0 ? `Check ${invalid.length === 1 ? 'this address' : 'these addresses'}: ${invalid.join(', ')}` : ' '}
                onChange={(e) => dispatch({ type: 'SET_CONTACT', field: q.field, value: e.target.value })}
              />
            </Box>
          )
        })}
      </Stack>
    </StepCard>
  )
}
